import {
  getObjectShape,
  getZodDef,
  integrationSchemaRegistry,
  type IntegrationSchemaRegistryEntry,
  isOptionalSchema,
  unwrapTransparentSchema,
} from '@main/shared/utils';
import { Namespace, TFunction } from 'i18next';
import { FieldPath, FieldValues } from 'react-hook-form';
import z from 'zod/v4';
import { $ZodDiscriminatedUnionDef, $ZodEnumDef, $ZodType, $ZodUnionDef } from 'zod/v4/core';

import {
  FieldInputState,
  FieldSlotDescriptor,
  FormArrayElement,
  FormFieldNode,
  FormOption,
  FormUnionOption,
  TextFieldInputState,
} from './model';

type TranslateFunction = TFunction<Namespace>;

export type SchemaFieldPolicyRole = 'field' | 'arrayElementPrototype';

export type FieldPolicyInput<
  TValues extends FieldValues,
  TMeta,
  TSlot extends FieldSlotDescriptor = never,
> = {
  schema: z.ZodType;
  path: readonly string[];
  name: string;
  role: SchemaFieldPolicyRole;
  required: FormFieldNode<TValues, TMeta, TSlot>['required'];
};

export type SchemaFieldPolicy<
  TValues extends FieldValues,
  TMeta,
  TSlot extends FieldSlotDescriptor = never,
> = (
  input: FieldPolicyInput<TValues, TMeta, TSlot>,
) => Partial<FormFieldNode<TValues, TMeta, TSlot>> | void;

export type CreateSchemaFieldModelInput<
  TValues extends FieldValues,
  TMeta = never,
  TSlot extends FieldSlotDescriptor = never,
> = {
  schema: z.ZodObject;
  namePrefix: string;
  pathPrefix: readonly string[];
  t: TranslateFunction;
  tKey: string;
  placeholder?: string;
  fieldPolicy?: SchemaFieldPolicy<TValues, TMeta, TSlot>;
};

type BuilderContext<
  TValues extends FieldValues,
  TMeta,
  TSlot extends FieldSlotDescriptor = never,
> = {
  t: TranslateFunction;
  placeholder?: string;
  fieldPolicy?: SchemaFieldPolicy<TValues, TMeta, TSlot>;
};

type NodeAddress = {
  role: SchemaFieldPolicyRole;
  name: string;
  path: readonly string[];
  labelKey: string;
  descriptionKey: string;
  labelDefault: string;
  tKey: string;
  namePrefix: string;
};

export function createSchemaFieldModel<
  TValues extends FieldValues = FieldValues,
  TMeta = never,
  TSlot extends FieldSlotDescriptor = never,
>({ schema, ...input }: CreateSchemaFieldModelInput<TValues, TMeta, TSlot>) {
  return buildFieldGroup({
    ...input,
    shape: getObjectShape(schema),
  });
}

function buildFieldGroup<
  TValues extends FieldValues,
  TMeta,
  TSlot extends FieldSlotDescriptor,
>(input: {
  shape: Record<string, z.ZodType>;
  namePrefix: string;
  pathPrefix: readonly string[];
  t: TranslateFunction;
  tKey: string;
  placeholder?: string;
  fieldPolicy?: SchemaFieldPolicy<TValues, TMeta, TSlot>;
}) {
  return Object.entries(input.shape).map(([key, schema]) =>
    buildNode({
      t: input.t,
      placeholder: input.placeholder,
      fieldPolicy: input.fieldPolicy,
      schema,
      address: buildFieldAddress(input, key),
    }),
  );
}

function buildFieldAddress(
  input: {
    namePrefix: string;
    pathPrefix: readonly string[];
    tKey: string;
  },
  key: string,
): NodeAddress {
  const path = [...input.pathPrefix, key];
  const name = [input.namePrefix, ...path].filter(Boolean).join('.');
  const fieldTKey = [input.tKey, key].filter(Boolean).join('.');

  return {
    role: 'field',
    name,
    path,
    labelKey: `${fieldTKey}.name`,
    descriptionKey: `${fieldTKey}.description`,
    labelDefault: key,
    tKey: fieldTKey,
    namePrefix: input.namePrefix,
  };
}

function buildArrayElementAddress(input: {
  name: string;
  path: readonly string[];
  tKey: string;
  namePrefix: string;
}): NodeAddress {
  return {
    role: 'arrayElementPrototype',
    name: input.name,
    path: input.path,
    labelKey: `${input.tKey}.name`,
    descriptionKey: `${input.tKey}.description`,
    labelDefault: input.path.at(-1) ?? input.name,
    tKey: input.tKey,
    namePrefix: input.namePrefix,
  };
}

function buildNode<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>({
  schema,
  address,
  t,
  placeholder,
  fieldPolicy,
}: BuilderContext<TValues, TMeta, TSlot> & {
  schema: z.ZodType;
  address: NodeAddress;
}): FormFieldNode<TValues, TMeta, TSlot> {
  const registryEntry = getRegistryEntry(schema);
  const effectiveTKey = registryEntry?.i18n ?? address.tKey;
  const label = t(address.labelKey, { defaultValue: address.labelDefault });
  const description = t(address.descriptionKey, { defaultValue: '' });
  const transparentSchema = unwrapTransparentSchema(schema);
  const def = getZodDef(transparentSchema);
  assertSupportedPresentation(registryEntry, def, address.name);
  const contractRequired = !isOptionalSchema(schema);
  const required = {
    contract: contractRequired,
    input: contractRequired && def.type !== 'boolean',
  };
  const base = {
    id: address.name,
    name: address.name as FieldPath<TValues>,
    path: address.path,
    label,
    required,
    ...(description ? { description } : {}),
    ...(placeholder && required.input ? { input: { placeholder } } : {}),
  };

  const node = (() => {
    if (registryEntry?.component) {
      return {
        ...base,
        kind: 'custom',
        component: registryEntry.component,
      } satisfies FormFieldNode<TValues, TMeta, TSlot>;
    }

    switch (def.type) {
      case 'string': {
        const input = buildFieldInput(base.input, registryEntry?.presentation);

        return {
          ...base,
          kind: 'text',
          ...(input ? { input } : {}),
        } satisfies FormFieldNode<TValues, TMeta, TSlot>;
      }
      case 'number': {
        return {
          ...base,
          kind: 'number',
        } satisfies FormFieldNode<TValues, TMeta, TSlot>;
      }
      case 'boolean': {
        return {
          ...base,
          kind: 'boolean',
        } satisfies FormFieldNode<TValues, TMeta, TSlot>;
      }
      case 'date': {
        return {
          ...base,
          kind: 'date',
        } satisfies FormFieldNode<TValues, TMeta, TSlot>;
      }
      case 'enum': {
        return {
          ...base,
          kind: 'enum',
          options: buildEnumOptions(t, effectiveTKey, def),
        } satisfies FormFieldNode<TValues, TMeta, TSlot>;
      }
      case 'object': {
        const shape = getObjectShape(transparentSchema as z.ZodObject);
        const children = buildFieldGroup<TValues, TMeta, TSlot>({
          shape,
          namePrefix: address.namePrefix,
          pathPrefix: address.path,
          t,
          tKey: `${effectiveTKey}.fields`,
          placeholder,
          fieldPolicy,
        });

        return {
          ...base,
          kind: 'object',
          switchable: !contractRequired,
          switchOnly: children.length === 0,
          children,
        } satisfies FormFieldNode<TValues, TMeta, TSlot>;
      }
      case 'array': {
        const elementSchema = def.element as z.ZodType;
        return {
          ...base,
          kind: 'array',
          element: buildArrayElement<TValues, TMeta, TSlot>({
            schema: elementSchema,
            name: address.name,
            path: address.path,
            namePrefix: address.namePrefix,
            t,
            tKey: effectiveTKey,
            placeholder,
            fieldPolicy,
          }),
        } satisfies FormFieldNode<TValues, TMeta, TSlot>;
      }
      case 'union': {
        assertDiscriminatedUnionDef(def, address.name);
        return {
          ...base,
          kind: 'union',
          discriminator: def.discriminator,
          options: buildUnionOptions<TValues, TMeta, TSlot>({
            def,
            name: address.name,
            namePrefix: address.namePrefix,
            path: address.path,
            t,
            tKey: effectiveTKey,
            placeholder,
            fieldPolicy,
          }),
        } satisfies FormFieldNode<TValues, TMeta, TSlot>;
      }
      default: {
        throw new Error(`Unsupported zod schema type "${def.type}" for field "${address.name}"`);
      }
    }
  })();

  return applyFieldPolicy(
    node,
    fieldPolicy?.({
      schema,
      path: address.path,
      name: address.name,
      role: address.role,
      required,
    }),
  );
}

function buildArrayElement<
  TValues extends FieldValues,
  TMeta,
  TSlot extends FieldSlotDescriptor,
>(input: {
  schema: z.ZodType;
  name: string;
  path: readonly string[];
  namePrefix: string;
  t: TranslateFunction;
  tKey: string;
  placeholder?: string;
  fieldPolicy?: SchemaFieldPolicy<TValues, TMeta, TSlot>;
}): FormArrayElement<TValues, TMeta, TSlot> {
  const elementNode = buildNode<TValues, TMeta, TSlot>({
    schema: input.schema,
    address: buildArrayElementAddress(input),
    t: input.t,
    placeholder: input.placeholder,
    fieldPolicy: input.fieldPolicy,
  });

  if (elementNode.kind === 'array') {
    throw new Error(`Unsupported zod schema type "array" for field "${input.name}"`);
  }

  return elementNode;
}

function buildEnumOptions(
  t: TranslateFunction,
  tKey: string,
  def: $ZodEnumDef,
): readonly FormOption[] {
  return Object.values(def.entries)
    .filter((value): value is string => typeof value === 'string')
    .map((value) => ({
      value,
      label: t(`${tKey}.${value}`, { defaultValue: value }),
    }));
}

function buildUnionOptions<
  TValues extends FieldValues,
  TMeta,
  TSlot extends FieldSlotDescriptor,
>(input: {
  def: $ZodDiscriminatedUnionDef;
  name: string;
  namePrefix: string;
  path: readonly string[];
  t: TranslateFunction;
  tKey: string;
  placeholder?: string;
  fieldPolicy?: SchemaFieldPolicy<TValues, TMeta, TSlot>;
}): readonly FormUnionOption<TValues, TMeta, TSlot>[] {
  const { def, name, namePrefix, path, t, tKey, placeholder, fieldPolicy } = input;

  return def.options.flatMap((option) => {
    const optionSchema = option as z.ZodType;
    const optionDef = getZodDef(option);
    if (optionDef.type !== 'object') {
      return [];
    }

    const discriminatorSchema = optionDef.shape[def.discriminator] as z.ZodType | undefined;
    if (!discriminatorSchema) {
      return [];
    }

    const discriminatorValues = getDiscriminatorValues(discriminatorSchema, name);
    const discriminatorTKey = getRegistryEntry(discriminatorSchema)?.i18n;
    const optionTKey =
      getRegistryEntry(optionSchema)?.i18n ?? `${tKey}.options.${discriminatorValues.join('|')}`;
    const { [def.discriminator]: _, ...optionShape } = optionDef.shape as Record<string, z.ZodType>;

    return discriminatorValues.map((value) => ({
      value,
      label: discriminatorTKey
        ? t(`${discriminatorTKey}.${value}`, { defaultValue: value })
        : value,
      fields: buildFieldGroup<TValues, TMeta, TSlot>({
        shape: optionShape,
        namePrefix,
        pathPrefix: path,
        t,
        tKey: optionTKey,
        placeholder,
        fieldPolicy,
      }),
    }));
  });
}

function getDiscriminatorValues(schema: z.ZodType, field: string): string[] {
  const def = getZodDef(schema);

  switch (def.type) {
    case 'literal':
      return getStringDiscriminatorValues(def.values, field);
    case 'enum':
      return getStringDiscriminatorValues(Object.values(def.entries), field);
    default:
      throw new Error(`Unsupported discriminator type "${def.type}" for field "${field}"`);
  }
}

function getStringDiscriminatorValues(values: readonly unknown[], field: string): string[] {
  if (values.length === 0 || values.some((value) => typeof value !== 'string')) {
    throw new Error(
      `Unsupported discriminator value for field "${field}", expected string literal or enum value`,
    );
  }

  return values as string[];
}

function buildFieldInput(
  baseInput: FieldInputState | undefined,
  presentation: IntegrationSchemaRegistryEntry['presentation'] | undefined,
): TextFieldInputState | undefined {
  const input = {
    ...baseInput,
    ...(presentation?.input === 'textarea' ? { control: 'textarea' as const } : {}),
  };

  return Object.keys(input).length > 0 ? input : undefined;
}

function assertSupportedPresentation(
  registryEntry: ReturnType<typeof getRegistryEntry>,
  def: ReturnType<typeof getZodDef>,
  field: string,
) {
  if (registryEntry?.presentation?.input === 'textarea' && def.type !== 'string') {
    throw new Error(`Unsupported textarea presentation for non-string field "${field}"`);
  }
}

function getRegistryEntry(schema: z.ZodType | $ZodType) {
  const transparentSchema = unwrapTransparentSchema(schema);
  return (
    integrationSchemaRegistry.get(schema as z.ZodType) ??
    integrationSchemaRegistry.get(transparentSchema)
  );
}

function applyFieldPolicy<TValues extends FieldValues, TMeta, TSlot extends FieldSlotDescriptor>(
  node: FormFieldNode<TValues, TMeta, TSlot>,
  policy?: Partial<FormFieldNode<TValues, TMeta, TSlot>> | void,
): FormFieldNode<TValues, TMeta, TSlot> {
  if (!policy) {
    return node;
  }

  const merged = { ...node, ...policy } as FormFieldNode<TValues, TMeta, TSlot>;

  if (node.input || policy.input) {
    return {
      ...merged,
      input: {
        ...node.input,
        ...policy.input,
      },
    };
  }

  return merged;
}

function assertDiscriminatedUnionDef(
  def: $ZodUnionDef,
  field: string,
): asserts def is $ZodDiscriminatedUnionDef {
  if (!('discriminator' in def && typeof def.discriminator === 'string')) {
    throw new Error(
      `Unsupported non-discriminated zod union schema for field "${field}", use discriminated union instead.`,
    );
  }
}
